import { MessageMedium } from 'constants/messageMediums';
import { selectFeatureFlags } from 'core/userContext';
import {
  CAMPAIGN_EDITOR_FORM_FIELDS,
  NO_PROJECT_ALERTS_TOOLTIP_CONTENT,
  PRIORITY_DISPLAY_VALUES,
} from 'modules/campaigns/editor/constants';
import { useIgnoreCapAvailability } from 'modules/campaigns/editor/hooks/useIgnoreCapAvailability';
import { BoldText } from 'modules/campaigns/editor/styles';
import { getShouldShowPerformanceAlerts } from 'modules/campaigns/editor/utils';
import { EnabledAlertsList } from 'modules/campaigns/lib/components/EnabledAlertsList';
import { DEFAULT_RATE_LIMIT_PER_MINUTE } from 'modules/campaigns/lib/constants';
import { useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { OpenNewWindow16 } from 'ui/icons';
import { colors } from 'ui/theme';
import { to12HourClock } from 'utils/time';
import { useCampaignAlertsContext, useCampaignContext, useEditorContext } from '../../../context';
import { CampaignFormField, ReadOnlyField } from '../../FormFields';
import { LightText, SectionHeader, SectionWrapper } from '../Sections.styled';
import { EditableAlerts } from './EditableAlerts';
import { EditablePriorityLevel } from './EditablePriorityLevel';
import { EditableQuietHours } from './EditableQuietHours';
import { EditableRateLimiting } from './EditableRateLimiting';
import { EditableSendTimeOptimization } from './EditableSendTimeOptimization';
import { ExpandableToggle } from './ExpandableToggle';
import { Caption, CaptionLink, LabelIconWrapper } from './OptimizeSection.styles';
import {
  OPTIMIZE_SECTION_FIELD_LABEL_ICONS_MAPPER,
  OptimizeSectionField,
  STO_SUPPORTED_MEDIUMS,
} from './constants';

export const OPTIMIZE_SECTION_TEST_ID = 'optimize-section';
export const QUIET_HOURS_LABEL_ICON = `campaign-${OptimizeSectionField.QuietHours}-icon`;
export const STO_LABEL_ICON = `campaign-${OptimizeSectionField.SendTimeOptimization}-icon`;

const IgnoreFrequencyCapCaption = () => (
  <>
    Send this message regardless of any frequency caps.{' '}
    <CaptionLink
      href="https://support.iterable.com/hc/articles/15342990564372"
      rel="noreferrer noopener"
      target="_blank"
    >
      <span>Learn more</span>
      <OpenNewWindow16 color={colors.textLink} />
    </CaptionLink>
  </>
);

const OptimizeSectionFieldLabel = ({
  field,
  labelText,
}: {
  field: Exclude<
    OptimizeSectionField,
    OptimizeSectionField.SendRateLimit | OptimizeSectionField.DisplayPriority
  >;
  labelText: string;
}) => {
  const Icon = OPTIMIZE_SECTION_FIELD_LABEL_ICONS_MAPPER[field];
  return (
    <>
      <LabelIconWrapper>
        {!!Icon && <Icon data-test={`campaign-${field}-icon`} width={16} height={16} />}
      </LabelIconWrapper>
      {labelText}
    </>
  );
};

export const OptimizeSection = () => {
  const { campaignSteps } = useEditorContext();
  const { campaign } = useCampaignContext();
  const { alertDetails, hasProjectAlerts } = useCampaignAlertsContext();

  const [
    ignoreFrequencyCap,
    useQuietHours,
    customQuietHours,
    useRateLimit,
    rateLimit,
    useSendTimeOptimization,
    stoCompletionTime,
    priorityLevel,
    campaignAlertsEnabled,
  ] = useWatch({
    name: [
      CAMPAIGN_EDITOR_FORM_FIELDS.IGNORE_FREQUENCY_CAP,
      CAMPAIGN_EDITOR_FORM_FIELDS.USE_QUIET_HOURS,
      CAMPAIGN_EDITOR_FORM_FIELDS.CUSTOM_QUIET_HOURS,
      CAMPAIGN_EDITOR_FORM_FIELDS.USE_RATE_LIMIT,
      CAMPAIGN_EDITOR_FORM_FIELDS.RATE_LIMIT,
      CAMPAIGN_EDITOR_FORM_FIELDS.USE_SEND_TIME_OPTIMIZATION,
      CAMPAIGN_EDITOR_FORM_FIELDS.STO_COMPLETION_TIME,
      CAMPAIGN_EDITOR_FORM_FIELDS.PRIORITY_LEVEL,
      CAMPAIGN_EDITOR_FORM_FIELDS.CAMPAIGN_ALERTS_ENABLED,
    ],
  });

  const isEditable = campaignSteps?.Delivery?.isEditable;

  const messageMedium = campaign?.messageMedium;
  const sendRateLimit = rateLimit?.rateLimitValue;
  const campaignType = campaign?.campaignTypeDefinition;
  const isJourneyAssociated = campaign?.isJourneyAssociated;

  const {
    CampaignRateLimiting: isRateLimitingFeatureEnabled,
    SendTimeOptimization: isSendTimeOptimizationFeatureEnabled,
    QuietHours: isQuietHoursFeatureEnabled,
    CampaignPerformanceAlerts: isCampaignPerformanceAlertsEnabled,
    JourneysCampaignPerformanceAlerts: isJourneysCampaignPerformanceAlertsEnabled,
  } = useSelector(selectFeatureFlags);

  const { isIgnoreCapVisible, isIgnoreCapEditable } = useIgnoreCapAvailability();

  const isStoSupportedMedium = STO_SUPPORTED_MEDIUMS.includes(messageMedium);

  /** We only want to show the feature below if the respective flag is on. */
  const showQuietHours = isQuietHoursFeatureEnabled;
  const showSTO = isStoSupportedMedium && isSendTimeOptimizationFeatureEnabled;
  const showRateLimiting = isRateLimitingFeatureEnabled;
  const showPerformanceAlerts = getShouldShowPerformanceAlerts({
    campaignType,
    isCampaignPerformanceAlertsEnabled,
    isJourneysCampaignPerformanceAlertsEnabled,
    isJourneyAssociated,
  });

  const isAlertsToggleDisabled = !hasProjectAlerts;
  const hasDeliverySettings =
    showQuietHours || showSTO || showRateLimiting || isIgnoreCapVisible || showPerformanceAlerts;

  /** If no optimizations are available, then don't show anything */
  if (!hasDeliverySettings) {
    return <></>;
  }

  return (
    <SectionWrapper data-test={OPTIMIZE_SECTION_TEST_ID}>
      <SectionHeader>Optimize</SectionHeader>
      {messageMedium === MessageMedium.InApp && (
        <CampaignFormField labelText="Display priority">
          {isEditable ? (
            <EditablePriorityLevel />
          ) : (
            <ReadOnlyField name="displayPriority">
              {PRIORITY_DISPLAY_VALUES[priorityLevel]}
            </ReadOnlyField>
          )}
        </CampaignFormField>
      )}
      {showQuietHours && (
        <CampaignFormField
          labelText={
            <OptimizeSectionFieldLabel
              labelText="Quiet hours"
              field={OptimizeSectionField.QuietHours}
            />
          }
        >
          {isEditable ? (
            <EditableQuietHours />
          ) : (
            <ReadOnlyField name="quietHours">
              {useQuietHours ? (
                <p>
                  Avoid sending&nbsp;
                  <BoldText>{to12HourClock(customQuietHours?.startTime as string, true)}</BoldText>
                  &nbsp;to&nbsp;
                  <BoldText>{to12HourClock(customQuietHours?.endTime as string, true)}</BoldText>
                  &nbsp;in user’s time zone
                </p>
              ) : (
                <LightText>Off</LightText>
              )}
            </ReadOnlyField>
          )}
        </CampaignFormField>
      )}

      {showSTO && (
        <CampaignFormField
          labelText={
            <OptimizeSectionFieldLabel
              labelText="Send time optimization"
              field={OptimizeSectionField.SendTimeOptimization}
            />
          }
        >
          {isEditable ? (
            <EditableSendTimeOptimization />
          ) : (
            <ReadOnlyField name="sendTimeOptimization">
              {useSendTimeOptimization ? (
                <p>
                  Complete send within <BoldText>{stoCompletionTime} hours</BoldText>
                </p>
              ) : (
                <LightText>Off</LightText>
              )}
            </ReadOnlyField>
          )}
        </CampaignFormField>
      )}

      {showRateLimiting && (
        <CampaignFormField labelText="Set a send rate limit">
          {isEditable ? (
            <EditableRateLimiting />
          ) : (
            <ReadOnlyField name="sendRateLimit">
              {useRateLimit ? (
                <p>
                  Override send rate limit for this campaign to&nbsp;
                  <BoldText>{sendRateLimit?.toLocaleString()} per minute</BoldText>
                </p>
              ) : (
                <LightText>{`Off – Default (${DEFAULT_RATE_LIMIT_PER_MINUTE.toLocaleString()} messages per minute)`}</LightText>
              )}
            </ReadOnlyField>
          )}
        </CampaignFormField>
      )}

      {isIgnoreCapVisible && (
        <CampaignFormField labelText="Ignore frequency cap">
          {isEditable ? (
            <ExpandableToggle
              name="ignoreFrequencyCap"
              caption={<IgnoreFrequencyCapCaption />}
              isDisabled={!isIgnoreCapEditable}
              tooltipContent={
                !isIgnoreCapEditable
                  ? 'There are no frequency caps set for this channel or message type'
                  : undefined
              }
            />
          ) : (
            <ReadOnlyField name="ignoreFrequencyCap">
              {ignoreFrequencyCap ? (
                <div>
                  <p>On</p>
                  <Caption>
                    <IgnoreFrequencyCapCaption />
                  </Caption>
                </div>
              ) : (
                <LightText>Off</LightText>
              )}
            </ReadOnlyField>
          )}
        </CampaignFormField>
      )}

      {showPerformanceAlerts && (
        <CampaignFormField labelText="Alerts">
          {isEditable ? (
            <EditableAlerts
              isDisabled={isAlertsToggleDisabled}
              tooltipContent={NO_PROJECT_ALERTS_TOOLTIP_CONTENT}
            />
          ) : (
            <ReadOnlyField name={CAMPAIGN_EDITOR_FORM_FIELDS.CAMPAIGN_ALERTS_ENABLED}>
              {campaignAlertsEnabled ? (
                <EnabledAlertsList alertDetails={alertDetails} defaultValue={<p>On</p>} />
              ) : (
                <LightText>Off</LightText>
              )}
            </ReadOnlyField>
          )}
        </CampaignFormField>
      )}
    </SectionWrapper>
  );
};
